/**
 * Scoreboard clock for the broadcast page. Same formulas as the shared
 * page's inline derivation (PublicMatchAnalytics effectiveTime /
 * timerFormulas / broadcastClock), expressed as a pure function of an
 * explicit `nowMs` so the delayed overlay can compute "as of playback time"
 * (now + server offset - data delay).
 */
import type { MatchEvent } from "../../store/useMatchLogStore";
import type { Match } from "../logger/types";
import {
  computePeriodElapsedSeconds,
  statusToPhase,
  type PeriodPhase,
} from "../logger/hooks/usePeriodManager";
import {
  computeBroadcastClock,
  resolveBroadcastArgument,
  type BroadcastClock,
} from "../logger/lib/clockHelpers";
import {
  computeIneffectiveBreakdown,
  computeTimerFormulas,
} from "../logger/utils";
import { parseServerMs } from "./serverTime";

const LIVE_STATUSES = new Set([
  "Live_First_Half",
  "Live_Second_Half",
  "Live_Extra_First",
  "Live_Extra_Second",
  "Penalties",
]);

const LIVE_PHASES: PeriodPhase[] = [
  "FIRST_HALF",
  "SECOND_HALF",
  "FIRST_HALF_EXTRA_TIME",
  "SECOND_HALF_EXTRA_TIME",
];

export const PERIOD_LABEL_KEY: Partial<Record<PeriodPhase, string>> = {
  FIRST_HALF: "period1",
  SECOND_HALF: "period2",
  FIRST_HALF_EXTRA_TIME: "period3",
  SECOND_HALF_EXTRA_TIME: "period4",
  PENALTIES: "penalties",
  COMPLETED: "fullTime",
};

export interface ScoreboardClock {
  clock: BroadcastClock;
  phase: PeriodPhase;
}

export function computeScoreboardClock(
  match: Match,
  events: MatchEvent[],
  nowMs: number,
  lastLiveAddedSeconds = 0,
): ScoreboardClock {
  const live = LIVE_STATUSES.has(match.status);
  let effectiveTime: number;
  if (
    live &&
    match.current_period_start_timestamp &&
    match.clock_mode === "EFFECTIVE"
  ) {
    const periodStart = parseServerMs(match.current_period_start_timestamp);
    effectiveTime =
      (match.clock_seconds_at_period_start ?? 0) +
      Math.max(0, (nowMs - periodStart) / 1000);
  } else {
    effectiveTime =
      match.clock_seconds_at_period_start ?? match.match_time_seconds ?? 0;
  }

  const breakdown = computeIneffectiveBreakdown(
    events,
    match.home_team.team_id ?? "",
    match.away_team.team_id ?? "",
  );
  const varSeconds = breakdown?.varActive
    ? Math.max(0, (nowMs - breakdown.varActive.startMs) / 1000)
    : 0;

  let ineffective = match.ineffective_time_seconds ?? 0;
  if (
    live &&
    match.current_period_start_timestamp &&
    match.clock_mode === "INEFFECTIVE" &&
    match.last_mode_change_timestamp
  ) {
    ineffective +=
      Math.max(0, nowMs - parseServerMs(match.last_mode_change_timestamp)) /
      1000;
  }

  const formulas = breakdown
    ? computeTimerFormulas({
        effectiveTime,
        ineffectiveSeconds: ineffective,
        timeoutSeconds: breakdown.timeout?.totalSeconds ?? 0,
        varTimeSeconds: varSeconds,
        teamIneffective: {
          home: breakdown.totals.home,
          away: breakdown.totals.away,
        },
      })
    : null;
  const globalSeconds = formulas?.globalSeconds ?? effectiveTime;

  const phase: PeriodPhase = statusToPhase(match.status) ?? "NOT_STARTED";
  const clock = LIVE_PHASES.includes(phase)
    ? computeBroadcastClock(
        phase,
        computePeriodElapsedSeconds(
          phase,
          globalSeconds,
          match.period_timestamps,
        ),
      )
    : computeBroadcastClock(
        phase,
        resolveBroadcastArgument(
          phase,
          effectiveTime,
          globalSeconds,
          lastLiveAddedSeconds,
        ),
      );
  return { clock, phase };
}
